/**
 * UnsplashPhoto + UnsplashCredit
 *
 * Two building blocks for using Unsplash photos anywhere on Social
 * Pulse in a way that always shows the attribution the Unsplash API
 * guidelines demand:
 *
 *     Photo by <Photographer> on Unsplash
 *
 * where both `<Photographer>` and `Unsplash` are links carrying the
 * required UTM params
 * (`?utm_source=social_pulse&utm_medium=referral&utm_campaign=api-credit`).
 *
 * ─── Use cases ───
 *
 * 1. You already have a photo object (from the backend
 *    `/api/unsplash/search` proxy). Just render:
 *
 *      <UnsplashPhoto photo={photo} className="rounded-xl" />
 *
 * 2. You have a raw Unsplash URL scraped from a source (like the
 *    hero photo on the /what-is-food-pulse page). Pass the credit
 *    manually:
 *
 *      <UnsplashPhoto
 *        src={HERO_IMG}
 *        alt="Farmers market"
 *        credit={{
 *          photographer: { name: "Megan Thomas", username: "organicdesignco" },
 *        }}
 *      />
 *
 * 3. You just want the caption strip below an image you're rendering
 *    yourself:
 *
 *      <img src={..} />
 *      <UnsplashCredit
 *        photographer={{ name: "Megan Thomas", username: "organicdesignco" }}
 *      />
 *
 * Every rendered credit is always both linked AND stamped with UTM
 * params so the Unsplash contract is honoured without any per-caller
 * ceremony.
 */

const APP_UTM = "utm_source=social_pulse&utm_medium=referral&utm_campaign=api-credit";

/** Return a URL with the Unsplash UTM contract appended (idempotent —
 *  won't double-stamp URLs that already carry `utm_source`). */
function withUtm(url) {
  if (!url) return "";
  if (/[?&]utm_source=/.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${APP_UTM}`;
}

/** Build a photographer profile URL from a username when only a name
 *  was passed by the caller. */
function profileUrl(username) {
  return username ? `https://unsplash.com/@${username}` : "";
}

/**
 * UnsplashCredit — small, italicised, one-liner:
 *
 *     Photo by <Name> / Unsplash
 *
 * Accepts either a raw `photographer` object (shape shown below) OR
 * a full `credit` dict as returned by the backend `/api/unsplash/search`
 * endpoint (name + profile_url already stamped with UTM).
 */
export function UnsplashCredit({
  photographer,     // { name, username?, profile_url? }
  unsplashHref,     // optional — deep-link to the photo page on Unsplash
  className = "",
  align = "center", // "center" | "left" | "right"
  testid,
}) {
  if (!photographer?.name) return null;

  // Prefer backend-provided profile_url (already UTM'd) — fall back to
  // constructing one from the username, then wrapping with UTM.
  const profile = photographer.profile_url
    || (photographer.username ? withUtm(profileUrl(photographer.username)) : "");
  const site    = withUtm(unsplashHref || "https://unsplash.com/");

  const alignClass = align === "left" ? "text-left" : align === "right" ? "text-right" : "text-center";

  return (
    <figcaption
      className={`text-xs opacity-70 ${alignClass} ${className}`}
      data-testid={testid || "unsplash-credit"}
    >
      Photo by{" "}
      {profile ? (
        <a
          href={profile}
          target="_blank" rel="noopener noreferrer"
          className="underline hover:opacity-100"
          data-testid="unsplash-credit-photographer"
        >
          {photographer.name}
        </a>
      ) : (
        <span data-testid="unsplash-credit-photographer">{photographer.name}</span>
      )}
      {" "}/{" "}
      <a
        href={site}
        target="_blank" rel="noopener noreferrer"
        className="underline hover:opacity-100"
        data-testid="unsplash-credit-site"
      >
        Unsplash
      </a>
    </figcaption>
  );
}

/**
 * UnsplashPhoto — <img> wrapped in a <figure> with the credit
 * baked-in. Supports two shapes:
 *
 *   A) photo={<full object from /api/unsplash/search>}
 *   B) src=... + credit={{ photographer:{name,username} }}
 *
 * Any `className` you pass lands on the <img>. The wrapping <figure>
 * inherits Tailwind's default block behaviour.
 */
export default function UnsplashPhoto({
  photo,                    // shape from /api/unsplash/search — wins over src/alt
  src,
  alt = "",
  credit,                   // manual override — { photographer, unsplashHref }
  className = "",
  captionClassName = "mt-2",
  captionAlign = "center",
  testid,
}) {
  const imgSrc  = photo?.image_url || src;
  const imgAlt  = photo?.alt_description || alt;
  const pgph    = photo?.photographer || credit?.photographer;
  const linkTo  = photo?.unsplash_link || credit?.unsplashHref;

  if (!imgSrc) return null;

  return (
    <figure data-testid={testid || "unsplash-photo"}>
      <img
        src={imgSrc}
        alt={imgAlt}
        loading="lazy"
        className={className}
      />
      {pgph && (
        <UnsplashCredit
          photographer={pgph}
          unsplashHref={linkTo}
          align={captionAlign}
          className={captionClassName}
          testid={testid ? `${testid}-credit` : undefined}
        />
      )}
    </figure>
  );
}
